import { SkippedReviewsSort, SkippedReviewsSortField } from '../../../services/MetricsDashboardService'
import { SkippedReview, formatDateLabel } from '../../../types/metrics'
import useReviewsTableSort from '../../../hooks/useReviewsTableSort'
import InfoTooltip from '../../atoms/InfoTooltip'
import EmployeeCell from './EmployeeCell'
import ReviewsTableShell from './ReviewsTableShell'
import { SortableHeader } from './sortableHeader'
import { BaseReviewsTableProps } from './reviewsTableTypes'

type Props = BaseReviewsTableProps<SkippedReview, SkippedReviewsSort>

// Mirrors the backend default when the user hasn't picked a sort.
const DEFAULT_SORT: SkippedReviewsSort = 'skipDate,desc'

const SkippedReviewsTable = ({
  rows,
  loading,
  error,
  page,
  pageSize,
  totalElements,
  sort,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onRetry,
}: Props): JSX.Element => {
  const headerProps = useReviewsTableSort<SkippedReviewsSortField>(sort, DEFAULT_SORT, onSortChange)

  const headerCells = (
    <>
      <SortableHeader col="employeeName" label="Employee" {...headerProps} />
      <SortableHeader col="reviewerName" label="Reviewer" {...headerProps} />
      <SortableHeader col="periodName" label="Period" {...headerProps} />
      <SortableHeader col="skipDate" label="Skip Date" {...headerProps} />
      <SortableHeader col="reason" label="Reason" {...headerProps} />
      <th scope="col" className="py-3 text-left text-xl font-semibold text-stone-gray">
        Notes
      </th>
    </>
  )

  const bodyRows = rows.map(row => (
    <tr key={row.skipId} className="odd:bg-gray-cell even:bg-gray-cell/50 hover:bg-just-gray">
      <EmployeeCell name={row.employeeName} email={row.employeeEmail} />
      <td className="py-4 pr-4 text-blue-gray">{row.reviewerName}</td>
      <td className="py-4 pr-4 text-blue-gray">{row.periodName}</td>
      <td className="py-4 pr-4 text-blue-gray">{formatDateLabel(row.skipDate)}</td>
      <td className="py-4 pr-4 text-blue-gray" title={row.reason}>
        <span className="block max-w-md truncate">{row.reason}</span>
      </td>
      <td className="py-4">
        {row.notes && row.notes.trim() !== '' ? (
          <InfoTooltip text={row.notes} ariaLabel={`Notes from ${row.reviewerName}`} />
        ) : (
          <span className="text-stone-gray">—</span>
        )}
      </td>
    </tr>
  ))

  return (
    <ReviewsTableShell
      ariaLabel="Skipped reviews"
      caption="List of skipped reviews with employee, period, reason, and notes"
      navAriaLabel="Skipped reviews pagination"
      emptyText="No skipped reviews found"
      isEmpty={rows.length === 0}
      loading={loading}
      error={error}
      onRetry={onRetry}
      page={page}
      pageSize={pageSize}
      totalElements={totalElements}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      headerCells={headerCells}
      bodyRows={bodyRows}
    />
  )
}

export default SkippedReviewsTable
